import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, Plane, User, Clock, CheckCircle, XCircle,
  ChevronRight, Eye, EyeOff, Database, Calendar, ArrowRight,
  Download, Wifi, Shield, X, BarChart2, Package
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useDialog } from '../contexts/DialogContext';


// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

const CrewAccessImport = () => {
  const { alert } = useDialog();
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [importTypes, setImportTypes] = useState({
    roster: true,
    crew_info: true,
    open_time: true,
  });
  const [isImporting, setIsImporting] = useState(false);
  const [importStatus, setImportStatus] = useState('');
  const [statusUpdates, setStatusUpdates] = useState([]);
  const [importHistory, setImportHistory] = useState([]);
  const [availableTrips, setAvailableTrips] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const [importedFlights, setImportedFlights] = useState([]);
  const [showImportedFlights, setShowImportedFlights] = useState(false);

  const fetchImportHistory = useCallback(async () => {
    try {
      const response = await fetch('/api/crewaccess/import-history', getAuthHeaders());
      if (response.ok) {
        const data = await response.json();
        setImportHistory(data.imports);
      } else if (response.status === 401) {
        console.error('Authentication failed - please log in again');
      }
    } catch (error) {
      console.error('Error fetching import history:', error);
    }
  }, []);

  const fetchImportedFlights = async (sessionId) => {
    try {
      const response = await fetch(`/api/crewaccess/imported-flights/${sessionId}`, getAuthHeaders());
      if (response.ok) {
        const data = await response.json();
        setImportedFlights(data.flights);
        setShowImportedFlights(true);
        console.log(`📊 Loaded ${data.flights.length} imported flights from session ${sessionId}`);
      } else if (response.status === 401) {
        console.error('Authentication failed - please log in again');
      } else {
        console.error('Error fetching imported flights:', response.statusText);
      }
    } catch (error) {
      console.error('Error fetching imported flights:', error);
    }
  };

  const fetchRecentImportedFlights = async () => {
    try {
      const response = await fetch('/api/crewaccess/recent-imported-flights?limit=20', getAuthHeaders());
      if (response.ok) {
        const data = await response.json();
        setImportedFlights(data.flights);
        setShowImportedFlights(true);
        console.log(`📊 Loaded ${data.flights.length} recent imported flights`);
      } else if (response.status === 401) {
        console.error('Authentication failed - please log in again');
      }
    } catch (error) {
      console.error('Error fetching recent imported flights:', error);
    }
  };

  
  const fetchAvailableTrips = useCallback(async () => {
    try {
      const response = await fetch('/api/crewaccess/available-trips', getAuthHeaders());
      if (response.ok) {
        const data = await response.json();
        setAvailableTrips(data.trips);
      } else if (response.status === 401) {
        console.error('Authentication failed - please log in again');
      }
    } catch (error) {
      console.error('Error fetching available trips:', error);
    }
  }, []);

  // Load import history on component mount
  useEffect(() => {
    fetchImportHistory();
    fetchAvailableTrips();
  }, [fetchImportHistory, fetchAvailableTrips]);

  const handleImport = async () => {
    if (!credentials.username || !credentials.password) {
      toast.error('Please enter your CrewAccess username and password');
      return;
    }

    // Check if we have a valid token
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('You are not authenticated. Please log in again.');
      return;
    }

    setIsImporting(true);
    setImportStatus('Importing from CrewAccess...');
    setStatusUpdates([]);
    setImportResults(null);

    try {
      const selectedImportTypes = Object.keys(importTypes).filter(type => importTypes[type]);
      
      const response = await fetch('/api/crewaccess/import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          username: credentials.username,
          password: credentials.password,
          importTypes: selectedImportTypes
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setImportStatus('Import completed successfully!');
        setImportResults(result);
        
        // Create user-friendly status updates
        const friendlyUpdates = generateFriendlyUpdates(result.statusUpdates || [], result.results);
        setStatusUpdates(friendlyUpdates);
        
        // Refresh data
        fetchImportHistory();
        fetchAvailableTrips();

        // Fetch imported flights for this session if we have a sessionId
        if (result.summary && result.summary.sessionId) {
          await fetchImportedFlights(result.summary.sessionId);
        }

        // Show user-friendly success message
        const successMessage = generateSuccessMessage(result.summary, result.results);
        setTimeout(() => alert({ title: 'Import complete', message: successMessage }), 500);
        
      } else if (response.status === 401) {
        setImportStatus('Import failed: Authentication error - please log in again');
        toast.error('Authentication failed. Please log in again.');
      } else {
        setImportStatus(`Import failed: ${result.error}`);
      }
      
    } catch (error) {
      setImportStatus(`Import failed: ${error.message}`);
    } finally {
      setIsImporting(false);
    }
  };

  // Generate user-friendly progress updates
  const generateFriendlyUpdates = (rawUpdates, results) => {
    const friendly = [];
    
    friendly.push({
      timestamp: new Date().toISOString(),
      message: '🔐 Connected to CrewAccess successfully',
      type: 'success'
    });

    if (results.roster && results.roster.imported > 0) {
      friendly.push({
        timestamp: new Date().toISOString(),
        message: `✈️ Imported ${results.roster.imported} flights with perfect timezone conversion`,
        type: 'success'
      });
    }

    if (results.roster && results.roster.tailsUpdated > 0) {
      friendly.push({
        timestamp: new Date().toISOString(),
        message: `🔁 Updated tail numbers on ${results.roster.tailsUpdated} upcoming flights`,
        type: 'success'
      });
    }

    if (results.roster && results.roster.crewUpdated > 0) {
      friendly.push({
        timestamp: new Date().toISOString(),
        message: `👥 Updated crew on ${results.roster.crewUpdated} upcoming flights`,
        type: 'success'
      });
    }

    if (results.crewInfo && results.crewInfo.imported > 0) {
      friendly.push({
        timestamp: new Date().toISOString(),
        message: '👤 Updated your crew information and certifications',
        type: 'success'
      });
    }

    if (results.openTime) {
      friendly.push({
        timestamp: new Date().toISOString(),
        message: '⏰ Processed available open time trips',
        type: 'info'
      });
    }

    friendly.push({
      timestamp: new Date().toISOString(),
      message: '🎉 All CrewAccess data synchronized successfully!',
      type: 'success'
    });

    return friendly;
  };

  // Generate user-friendly success message
  const generateSuccessMessage = (summary, results) => {
    let message = '🎉 Import Completed Successfully!\n\n';
    
    if (summary.totalImported > 0) {
      message += `✅ ${summary.totalImported} new flights added to your logbook\n`;
    }
    
    if (results.roster && results.roster.imported > 0) {
      message += `📅 Flight schedules updated with accurate timezone conversion\n`;
    }
    
    if (results.roster && results.roster.tailsUpdated > 0) {
      message += `🔁 ${results.roster.tailsUpdated} upcoming flights got updated tail numbers\n`;
    }
    
    if (results.roster && results.roster.crewUpdated > 0) {
      message += `👥 ${results.roster.crewUpdated} upcoming flights got crew changes\n`;
    }
    
    if (results.crewInfo && results.crewInfo.imported > 0) {
      message += `👤 Crew information and certifications synchronized\n`;
    }
    
    if (summary.totalErrors > 0) {
      message += `\n⚠️ ${summary.totalErrors} items were skipped (likely duplicates or already imported)`;
    } else {
      message += '\n🎯 All available data processed successfully!';
    }
    
    return message;
  };

  const handleAcceptTrip = async (tripId) => {
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`/api/crewaccess/accept-trip/${tripId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (response.ok) {
        toast.success(`Trip accepted: ${result.message}`);
        fetchAvailableTrips(); // Refresh the list
      } else if (response.status === 401) {
        toast.error('Authentication failed. Please log in again.');
      } else {
        toast.error(`Failed to accept trip: ${result.error}`);
      }
    } catch (error) {
      toast.error(`Error accepting trip: ${error.message}`);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  // Helper function to format flight times
  const formatFlightTime = (timeStr) => {
    if (!timeStr) return 'N/A';
    
    // Handle different time formats
    if (typeof timeStr === 'string') {
      // If it's HHMM format like "2045"
      if (timeStr.length === 4 && /^\d{4}$/.test(timeStr)) {
        const hours = timeStr.slice(0, 2);
        const minutes = timeStr.slice(2, 4);
        return `${hours}:${minutes}`;
      }
      
      // If it's already HH:MM format or datetime
      if (timeStr.includes(':')) {
        // Extract just the time part if it's a datetime
        const timePart = timeStr.includes('T') ? timeStr.split('T')[1] : timeStr;
        return timePart.slice(0, 5); // HH:MM
      }
    }
    
    return timeStr;
  };

  const formatFlightDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}:${mins.toString().padStart(2, '0')}`;
  };


  const [showPassword, setShowPassword] = useState(false);

  const importTypeConfig = {
    roster:    { label: 'Flight Roster',  icon: Plane,    description: 'Flights, trips & schedules' },
    crew_info: { label: 'Crew Info',      icon: User,     description: 'Certifications & qualifications' },
    open_time: { label: 'Open Time',      icon: Clock,    description: 'Available trip pickups' },
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

        {/* Page header */}
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-sky-600 rounded-xl shadow-sm">
            <Wifi className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">CrewAccess Sync</h1>
            <p className="text-sm text-slate-500">Import your roster, crew info, and open time directly from Alaska CrewAccess</p>
          </div>
        </div>

        {/* ── Main import card ─────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">

          {/* Card header */}
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 flex items-center space-x-2">
            <Shield className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-500">Credentials are used only to fetch your data and are never stored</span>
          </div>

          <div className="p-6 space-y-6">

            {/* Credentials */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Username</label>
                <input
                  type="text"
                  className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 bg-white placeholder-slate-400 disabled:bg-slate-50 disabled:text-slate-400"
                  value={credentials.username}
                  onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                  placeholder="Your CrewAccess username"
                  disabled={isImporting}
                  autoComplete="username"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="w-full px-3.5 py-2.5 pr-10 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 bg-white placeholder-slate-400 disabled:bg-slate-50 disabled:text-slate-400"
                    value={credentials.password}
                    onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Your CrewAccess password"
                    disabled={isImporting}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Import type toggles */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">Data to Import</label>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                {Object.keys(importTypes).map(type => {
                  const cfg = importTypeConfig[type];
                  const Icon = cfg?.icon || Database;
                  const checked = importTypes[type];
                  return (
                    <label
                      key={type}
                      className={`flex items-center space-x-2.5 cursor-pointer select-none ${isImporting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => setImportTypes(prev => ({ ...prev, [type]: e.target.checked }))}
                        disabled={isImporting}
                        className="w-4 h-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500 focus:ring-2 focus:ring-offset-1 cursor-pointer"
                      />
                      <Icon className={`h-4 w-4 flex-shrink-0 ${checked ? 'text-sky-600' : 'text-slate-400'}`} />
                      <span className={`text-sm font-medium ${checked ? 'text-gray-900' : 'text-slate-500'}`}>
                        {cfg?.label || type}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Import button */}
            <button
              onClick={handleImport}
              disabled={isImporting}
              className={`w-full flex items-center justify-center py-3 px-6 rounded-xl text-sm font-semibold transition-all ${
                isImporting
                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                  : 'bg-sky-600 hover:bg-sky-700 text-white shadow-sm hover:shadow'
              }`}
            >
              {isImporting ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Syncing with CrewAccess…</>
              ) : (
                <><Download className="h-4 w-4 mr-2" />Sync from CrewAccess</>
              )}
            </button>
          </div>

          {/* Loading progress */}
          {isImporting && (
            <div className="border-t border-slate-100 bg-slate-50 px-6 py-5">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-4">Sync Progress</p>
              <div className="space-y-3">
                {[
                  { label: 'Connecting to CrewAccess',        sub: 'Authenticating securely',                    opacity: 'opacity-100' },
                  { label: 'Downloading flight schedules',     sub: 'Roster, crew info & assignments',            opacity: 'opacity-75'  },
                  { label: 'Processing timezone conversions',  sub: 'Converting UTC to local times',              opacity: 'opacity-50'  },
                  { label: 'Saving to your logbook',           sub: 'Adding flights & updating crew assignments', opacity: 'opacity-25'  },
                ].map((step, i) => (
                  <div key={i} className={`flex items-center space-x-3 ${step.opacity}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${i === 0 ? 'bg-sky-500' : 'bg-slate-200'}`}>
                      {i === 0
                        ? <RefreshCw className="h-3.5 w-3.5 text-white animate-spin" />
                        : <div className="w-2 h-2 rounded-full bg-slate-400" />
                      }
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{step.label}</p>
                      <p className="text-xs text-slate-400">{step.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-4">This typically takes 1–2 minutes</p>
            </div>
          )}
        </div>

        {/* Error banner */}
        {importStatus.includes('failed') && (
          <div className="flex items-start space-x-3 p-4 bg-red-50 border border-red-200 rounded-xl">
            <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm font-medium text-red-800">{importStatus}</p>
          </div>
        )}

        {/* Import results */}
        {importResults && !isImporting && (
          <div className="space-y-4">
            <div className="flex items-center space-x-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
              <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-emerald-900">Sync completed successfully</p>
                <p className="text-xs text-emerald-700 mt-0.5">Your CrewAccess data has been synchronized with your logbook</p>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {importResults.results.roster && (
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="p-1.5 bg-sky-100 rounded-lg"><Plane className="h-4 w-4 text-sky-600" /></div>
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Flights</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{importResults.results.roster.imported}</p>
                  <p className="text-xs text-slate-500 mt-1">new flights imported</p>
                  {importResults.summary?.sessionId && (
                    <button onClick={() => fetchImportedFlights(importResults.summary.sessionId)}
                      className="mt-3 text-xs text-sky-600 hover:text-sky-800 font-semibold flex items-center">
                      View flights <ChevronRight className="h-3 w-3 ml-0.5" />
                    </button>
                  )}
                </div>
              )}
              {importResults.results.crewInfo && (
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="p-1.5 bg-sky-100 rounded-lg"><User className="h-4 w-4 text-sky-600" /></div>
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Crew Info</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">✓</p>
                  <p className="text-xs text-slate-500 mt-1">certifications updated</p>
                </div>
              )}
              {importResults.results.openTime && (
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="p-1.5 bg-sky-100 rounded-lg"><Clock className="h-4 w-4 text-sky-600" /></div>
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Open Time</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">✓</p>
                  <p className="text-xs text-slate-500 mt-1">available trips synced</p>
                </div>
              )}
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="p-1.5 bg-slate-100 rounded-lg"><BarChart2 className="h-4 w-4 text-slate-500" /></div>
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Summary</span>
                </div>
                <p className="text-3xl font-bold text-gray-900">{importResults.summary.totalImported}</p>
                <p className="text-xs text-slate-500 mt-1">total items imported</p>
                {importResults.summary.totalErrors > 0 && (
                  <p className="text-xs text-amber-600 mt-1">{importResults.summary.totalErrors} duplicates skipped</p>
                )}
              </div>
            </div>

            {statusUpdates.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Sync Log</p>
                </div>
                <div className="divide-y divide-slate-100">
                  {statusUpdates.map((update, i) => (
                    <div key={i} className="flex items-center px-5 py-3 space-x-3">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        update.type === 'success' ? 'bg-emerald-400' :
                        update.type === 'error'   ? 'bg-red-400' : 'bg-sky-400'
                      }`} />
                      <p className="text-sm text-gray-700 flex-1">{update.message.replace(/^[^\w]+ /, '')}</p>
                      <p className="text-xs text-slate-400 flex-shrink-0">{new Date(update.timestamp).toLocaleTimeString()}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Imported flights table */}
        {importedFlights.length > 0 && showImportedFlights && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Imported Flights</h2>
                <p className="text-xs text-slate-500 mt-0.5">{importedFlights.length} flight{importedFlights.length !== 1 ? 's' : ''} from latest sync</p>
              </div>
              <div className="flex items-center space-x-3">
                <button onClick={fetchRecentImportedFlights} className="text-xs text-sky-600 hover:text-sky-800 font-semibold">
                  View all recent
                </button>
                <button onClick={() => setShowImportedFlights(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    {['Date','Flight','Route','Times','Aircraft','Crew','Block'].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {importedFlights.map((flight, i) => (
                    <tr key={flight.id || i} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3.5 whitespace-nowrap font-medium text-gray-900">{formatFlightDate(flight.date)}</td>
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <p className="font-semibold text-gray-900">{flight.flightNumber}</p>
                        {flight.aircraft && <span className="inline-block mt-0.5 px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{flight.aircraft}</span>}
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <div className="flex items-center space-x-1.5 font-medium text-gray-900">
                          <span>{flight.departureAirport}</span>
                          <ArrowRight className="h-3 w-3 text-slate-400" />
                          <span>{flight.arrivalAirport}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-slate-500 font-mono text-xs">
                        <div>OUT {formatFlightTime(flight.scheduledOut)}</div>
                        <div>IN &nbsp;{formatFlightTime(flight.scheduledIn)}</div>
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-slate-500">{flight.aircraft || '—'}</td>
                      <td className="px-5 py-3.5 text-slate-500 text-xs space-y-0.5">
                        {flight.pic && <div><span className="text-slate-400">PIC </span>{flight.pic}</div>}
                        {flight.sic && <div><span className="text-slate-400">SIC </span>{flight.sic}</div>}
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap font-semibold text-gray-900">
                        {flight.blockTime ? `${flight.blockTime}h` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Available open time trips */}
        {availableTrips.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center space-x-2">
              <Package className="h-4 w-4 text-slate-400" />
              <h2 className="text-base font-semibold text-gray-900">Available Open Time</h2>
              <span className="ml-auto text-xs text-slate-400">{availableTrips.length} trips</span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    {['Trip','Date','Route','Duration','Credit',''].map((h, i) => (
                      <th key={i} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {availableTrips.slice(0, 10).map(trip => (
                    <tr key={trip.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <span className="font-semibold text-gray-900">{trip.trip_name}</span>
                        <span className="ml-2 px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded text-xs">{trip.role}</span>
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-slate-500">
                        {trip.dor ? new Date(trip.dor).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <div className="flex items-center space-x-1.5 font-medium text-gray-700">
                          <span>{trip.start_station}</span>
                          <ArrowRight className="h-3 w-3 text-slate-400" />
                          <span>{trip.end_station}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-slate-500">{formatDuration(trip.duration_minutes)}</td>
                      <td className="px-5 py-3.5 whitespace-nowrap font-semibold text-gray-900">
                        {trip.credit_value ? (trip.credit_value / 100).toFixed(2) : '—'}
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        {trip.is_pickable && (
                          <button onClick={() => handleAcceptTrip(trip.id)}
                            className="px-3 py-1.5 text-xs font-semibold text-sky-700 bg-sky-50 hover:bg-sky-100 border border-sky-200 rounded-lg transition-colors">
                            Accept
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Import history */}
        {importHistory.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-slate-400" />
              <h2 className="text-base font-semibold text-gray-900">Sync History</h2>
            </div>
            <div className="divide-y divide-slate-100">
              {importHistory.slice(0, 5).map(record => (
                <div key={record.id} className="px-6 py-4 flex items-center space-x-4">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    record.import_status === 'success' || record.import_status === 'completed'
                      ? 'bg-emerald-400' : record.import_status === 'failed' ? 'bg-red-400' : 'bg-amber-400'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-900 capitalize">
                        {record.import_type.replace('_', ' ')}
                      </span>
                      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                        record.import_status === 'success' || record.import_status === 'completed'
                          ? 'bg-emerald-100 text-emerald-700'
                          : record.import_status === 'failed'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {record.import_status}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{formatDateTime(record.created_at)}</p>
                  </div>
                  <div className="text-right text-xs text-slate-500 flex-shrink-0">
                    <p><span className="font-semibold text-gray-700">{record.records_imported || 0}</span> imported</p>
                    {record.records_failed > 0 && <p className="text-amber-600">{record.records_failed} skipped</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default CrewAccessImport;